var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DataCondition, } from "../../../contracts";
import { observable, action, makeObservable } from "mobx";
import { disposer, isAbortError } from "@draftkings/sportsbook-common-utils";
import { getFilterId, hasEventId, hasLeagueId, } from "../..";
import { SortedMap } from "../../Shared";
import { throttle } from "throttle-debounce";
import { throttleDefault } from "../helpers";
export class PrePacksRetriever {
    constructor(options) {
        this.processSubscriptionData = (payload, filterId) => {
            this.pushUpdatesQueue.push({
                filterId,
                payload
            });
            this.applyChangesThrottle();
        };
        this.applyChanges = () => {
            this.flushUpdates();
            if (this.events.size && this.filters.size) {
                this.setCondition("LOADED");
            }
            else {
                this.setCondition("EMPTY");
            }
            this.error = undefined;
        };
        this.onError = (options) => (error) => {
            if (error instanceof Error) {
                this.setError({
                    statusCode: "PrePacksQueryError",
                    description: error.message,
                    eventId: options.eventId,
                    leagueId: options.leagueId,
                });
            }
            else {
                this.setError({
                    eventId: options.eventId,
                    leagueId: options.leagueId,
                    statusCode: error.code,
                    description: error.message
                });
            }
        };
        this.onErrorCallback = (error) => {
            // Default description in case message is missing
            const description = error.message || "Unknown WebSocket connection error";
            this.setError({
                eventId: this.eventId,
                leagueId: this.leagueId,
                statusCode: "WebSocketConnectionError",
                description
            });
        };
        this.condition = options.condition;
        this.locale = options.locale;
        this.eventId = options.eventId;
        this.leagueId = options.leagueId;
        this.inGameState = options.inGameState;
        this.subscriptionDisposer = disposer;
        this.prePacksRequestResolver = options.prePacksRequestResolver;
        this.page = options.page;
        this.events = new Map();
        this.initialEvents = new Map();
        this.filters = new Map();
        this.prePacks = new Map();
        this.error = undefined;
        this.SubscribeToSportsbookPrePacks = options.SportsbookPrePacks;
        this.pushUpdatesQueue = [];
        this.throttleTimeout = options.throttleTimeout || 200;
        this.applyChangesThrottle = throttleDefault();
        makeObservable(this, {
            events: observable,
            initialEvents: observable,
            filters: observable,
            prePacks: observable,
            error: observable,
            onError: action,
            clearData: action,
            processQueryData: action,
            changeFilter: action,
            add: action,
            change: action,
            remove: action,
            getData: action,
            applyChanges: action,
        });
    }
    clearData() {
        this.events.clear();
        this.filters.clear();
        this.prePacks.clear();
        this.error = undefined;
    }
    activate() {
        this.loadData({
            eventId: this.eventId,
            leagueId: this.leagueId,
        });
    }
    deactivate() {
        this.pushUpdatesQueue = [];
        this.cancelThrottling();
        this.subscriptionDisposer();
        this.clearData();
        this.setCondition("LOADING");
    }
    setParams(params) {
        if (hasEventId(params)) {
            this.eventId = params.eventId;
        }
        else if (hasLeagueId(params)) {
            this.leagueId = params.leagueId;
        }
    }
    changeFilter(filterId) {
        const filter = this.filters.get(filterId);
        if (!filter) {
            return;
        }
        this.setCondition("LOADING");
        this.loadData(Object.assign(Object.assign({}, filter), { filterId }), false);
    }
    loadData(options, includeFilters = true) {
        this.pushUpdatesQueue = [];
        this.cancelThrottling();
        this.subscriptionDisposer();
        this.applyChangesThrottle = throttle(this.throttleTimeout, this.applyChanges);
        this.getData(options, includeFilters).then((data) => {
            const subscriptionPartial = data.data.subscriptionPartials[Object.keys(data.data.subscriptionPartials)[0]];
            if (subscriptionPartial) {
                this.subscriptionDisposer = this.subscribeForPrePacks(subscriptionPartial, options.filterId);
            }
        }).catch((error) => {
            if (!isAbortError(error)) {
                this.onError({ eventId: this.eventId, leagueId: this.leagueId })(error);
            }
        });
    }
    getData(options, includeFilters = true) {
        return __awaiter(this, void 0, void 0, function* () {
            this.prePacksRequestResolver.cancel();
            const data = yield this.prePacksRequestResolver.send({
                queryParams: {
                    eventId: getOption(options, "eventId"),
                    leagueId: getOption(options, "leagueId"),
                    includeFilters,
                    inGameState: this.inGameState,
                    locale: this.locale,
                    minAmericanOdds: getOption(options, "minAmericanOdds"),
                    maxAmericanOdds: getOption(options, "maxAmericanOdds"),
                    venueRole: getOption(options, "venueRole"),
                }
            });
            if (data) {
                this.processQueryData(data, getOption(options, "filterId"));
                if (this.events.size && this.filters.size) {
                    this.setCondition("LOADED");
                }
                else {
                    this.setCondition("EMPTY");
                }
                return data;
            }
            else {
                throw new Error(`Pre packs not found for event id: ${this.eventId} and league id: ${this.leagueId}`);
            }
        });
    }
    subscribeForPrePacks(options, filterId) {
        const prePacks = new this.SubscribeToSportsbookPrePacks(options, this.page, (payload) => this.processSubscriptionData(payload, filterId), this.onErrorCallback);
        return () => {
            prePacks.deactivate();
        };
    }
    flushUpdates() {
        while (this.pushUpdatesQueue.length) {
            const pushUpdate = this.pushUpdatesQueue.shift();
            if (pushUpdate) {
                this.add(pushUpdate.payload.data.add, pushUpdate.filterId);
                this.change(pushUpdate.payload.data.change, pushUpdate.filterId);
                this.remove(pushUpdate.payload.data.remove, pushUpdate.filterId);
            }
        }
    }
    add(payload, filterId) {
        var _a;
        payload.events.forEach(x => this.events.set(x.id, x));
        (_a = payload.filters) === null || _a === void 0 ? void 0 : _a.forEach(x => this.filters.set(getFilterId(x), x));
        const prePacksKey = this.getPrePacksKey(filterId);
        const prePacks = this.prePacks.get(prePacksKey);
        if (prePacks) {
            this.processAddPush(prePacks, payload.prePacks);
        }
        else {
            this.prePacks.set(prePacksKey, new SortedMap(payload.prePacks.map(p => [p.id, Object.assign(Object.assign({}, p), { isLocked: false, isInitialPull: false })])));
        }
    }
    change(payload, filterId) {
        const prePacks = this.prePacks.get(this.getPrePacksKey(filterId));
        if (!prePacks) {
            return;
        }
        payload.forEach(x => {
            const targetPrePack = prePacks.get(x.id);
            if (targetPrePack) {
                this.updatePrePack(targetPrePack, x);
            }
        });
    }
    remove(payload, filterId) {
        const prePacks = this.prePacks.get(this.getPrePacksKey(filterId));
        if (!prePacks) {
            return;
        }
        payload.forEach(id => prePacks.delete(id));
    }
    getPrePacksKey(filterId) {
        if (filterId) {
            return filterId;
        }
        return getFilterId([...this.filters.values()].find(x => x.isDefault));
    }
    processAddPush(prePacks, payload) {
        payload.forEach(p => {
            const existing = prePacks.get(p.id);
            if (existing) {
                this.updatePrePack(existing, p, false);
            }
            else {
                prePacks.insertOrReplace(p.id, p.replacedPrePackId, Object.assign(Object.assign({}, p), { isLocked: false, isInitialPull: false }));
            }
        });
    }
    updatePrePack(target, source, isLocked) {
        target.americanOdds = source.americanOdds;
        target.displayOdds = source.displayOdds;
        target.isSuspended = source.isSuspended;
        target.isLocked = typeof isLocked === "undefined" ? target.isLocked : isLocked;
    }
    setError(options) {
        const entityId = options.eventId || options.leagueId;
        this.error = Object.assign(Object.assign({}, (entityId ? { entityId, entityType: this.eventId ? "event" : "league" } : {})), { statusCode: options.statusCode, description: options.description });
        this.setCondition("ERROR");
    }
    processQueryData(payload, filterId) {
        var _a;
        payload.data.events.forEach(x => this.events.set(x.id, x));
        (_a = payload.data.filters) === null || _a === void 0 ? void 0 : _a.forEach(x => this.filters.set(getFilterId(x), x));
        this.processQueryPrePacksData(payload.data.prePacks, filterId);
    }
    processQueryPrePacksData(prePacks, filterId) {
        const prePacksKey = this.getPrePacksKey(filterId);
        this.prePacks.set(prePacksKey, new SortedMap(prePacks.map(p => [p.id, Object.assign(Object.assign({}, p), { isLocked: false, isInitialPull: true })])));
    }
    setCondition(value) {
        this.condition.set(PrePacksRetriever.ConditionKey, DataCondition[value]);
    }
    cancelThrottling() {
        this.applyChangesThrottle.cancel();
    }
}
PrePacksRetriever.ConditionKey = "PrePacks";
function getOption(options, property) {
    var _a;
    return ((_a = options[property]) === null || _a === void 0 ? void 0 : _a.toString()) || undefined;
}
//# sourceMappingURL=PrePacksRetriever.js.map